import { useState } from 'react';
import { getColorHex } from '../../../../hooks/useAdminClientePerfil';

interface Props {
  paletteColors: string[];
  combinacionesPersonalizadas: { base: string; complemento: string; tono: string; desc: string }[];
  setCombinacionesPersonalizadas: (v: { base: string; complemento: string; tono: string; desc: string }[] | ((prev: { base: string; complemento: string; tono: string; desc: string }[]) => { base: string; complemento: string; tono: string; desc: string }[])) => void;
  prendasQueCoincidenPaleta: { id: number | string; nombre: string; color: string }[];
}

const neutralColors = ['Blanco', 'Negro', 'Gris perla', 'Beige', 'Crudo', 'Oro', 'Plata', 'Cobre'];

export default function CombinacionesSection({
  paletteColors,
  combinacionesPersonalizadas,
  setCombinacionesPersonalizadas,
  prendasQueCoincidenPaleta,
}: Props) {
  const [combinacionesView, setCombinacionesView] = useState(false);

  if (!combinacionesView) {
    return (
      <div className="bg-white border border-brand-gray-200 rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brand-sage/20 flex items-center justify-center text-brand-black">
              <i className="ri-shuffle-line text-lg" />
            </div>
            <div>
              <h3 className="font-serif text-brand-black text-lg font-medium">Combinaciones</h3>
              <p className="font-sans text-brand-gray-500 text-xs mt-0.5">
                Looks sugeridos personalizados por Anthony
              </p>
            </div>
          </div>
          <button
            onClick={() => setCombinacionesView(true)}
            className="inline-flex items-center gap-1.5 font-sans text-sm px-3 py-2 rounded-lg border border-brand-gray-200 hover:border-brand-black hover:text-brand-black transition-colors"
          >
            <i className="ri-eye-line" />
            Ver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-brand-gray-200 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-brand-sage/20 flex items-center justify-center text-brand-black">
            <i className="ri-shuffle-line text-lg" />
          </div>
          <div>
            <h3 className="font-serif text-brand-black text-lg font-medium">Combinaciones</h3>
            <p className="font-sans text-brand-gray-500 text-xs mt-0.5">
              Looks sugeridos personalizados por Anthony
            </p>
          </div>
        </div>
        <button
          onClick={() => setCombinacionesView(false)}
          className="inline-flex items-center gap-1.5 font-sans text-sm px-3 py-2 rounded-lg border border-brand-gray-200 hover:border-brand-black hover:text-brand-black transition-colors"
        >
          <i className="ri-eye-off-line" />
          Ocultar
        </button>
      </div>

      <div className="space-y-4">
        {paletteColors.length < 2 ? (
          <div className="text-center py-6 text-brand-gray-400">
            <i className="ri-palette-line text-2xl mb-2" />
            <p className="font-sans text-sm">Selecciona al menos 2 colores para generar combinaciones</p>
          </div>
        ) : (
          <>
            {/* Auto combos */}
            <AutoCombinaciones paletteColors={paletteColors} />

            {/* Personalizadas */}
            <div className="space-y-3 pt-4 border-t border-brand-gray-200">
              <div className="flex items-center justify-between">
                <p className="font-sans text-brand-gray-500 text-xs uppercase tracking-wider">
                  Combinaciones personalizadas por Anthony
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setCombinacionesPersonalizadas((prev) => [
                      ...prev,
                      { base: paletteColors[0] || 'Negro', complemento: paletteColors[1] || 'Blanco', tono: 'Personalizado', desc: '' },
                    ]);
                  }}
                  className="inline-flex items-center gap-1.5 font-sans text-xs px-2.5 py-1.5 rounded-lg border border-brand-gray-200 hover:border-brand-black hover:text-brand-black transition-colors"
                >
                  <i className="ri-add-line" />
                  Añadir combinación
                </button>
              </div>

              {combinacionesPersonalizadas.length === 0 ? (
                <p className="font-sans text-sm text-brand-gray-400 py-2">
                  Aún no hay combinaciones personalizadas. Pulsa "Añadir combinación" para crear una.
                </p>
              ) : (
                <div className="space-y-3">
                  {combinacionesPersonalizadas.map((combo, idx) => (
                    <div key={idx} className="border border-brand-gray-200 rounded-xl p-4 bg-white">
                      <div className="flex items-start gap-3">
                        <div className="flex -space-x-2 shrink-0 pt-1">
                          <span
                            className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                            style={{ backgroundColor: getColorHex(combo.base) }}
                          />
                          <span
                            className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                            style={{ backgroundColor: getColorHex(combo.complemento) }}
                          />
                        </div>
                        <div className="flex-1 space-y-2.5 min-w-0">
                          <div className="flex flex-col sm:flex-row gap-2">
                            <select
                              value={combo.base}
                              onChange={(e) => {
                                setCombinacionesPersonalizadas((prev) =>
                                  prev.map((c, i) => i === idx ? { ...c, base: e.target.value } : c)
                                );
                              }}
                              className="flex-1 border border-brand-gray-200 rounded-lg px-3 py-1.5 font-sans text-sm focus:outline-none focus:border-brand-black"
                            >
                              {paletteColors.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                            <span className="text-brand-gray-400 font-sans text-sm self-center">+</span>
                            <select
                              value={combo.complemento}
                              onChange={(e) => {
                                setCombinacionesPersonalizadas((prev) =>
                                  prev.map((c, i) => i === idx ? { ...c, complemento: e.target.value } : c)
                                );
                              }}
                              className="flex-1 border border-brand-gray-200 rounded-lg px-3 py-1.5 font-sans text-sm focus:outline-none focus:border-brand-black"
                            >
                              {paletteColors.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          </div>
                          <input
                            type="text"
                            value={combo.tono}
                            onChange={(e) => {
                              setCombinacionesPersonalizadas((prev) =>
                                prev.map((c, i) => i === idx ? { ...c, tono: e.target.value } : c)
                              );
                            }}
                            placeholder="Tono (ej: Elegante, Casual...)"
                            className="w-full border border-brand-gray-200 rounded-lg px-3 py-1.5 font-sans text-sm focus:outline-none focus:border-brand-black"
                          />
                          <textarea
                            value={combo.desc}
                            onChange={(e) => {
                              setCombinacionesPersonalizadas((prev) =>
                                prev.map((c, i) => i === idx ? { ...c, desc: e.target.value } : c)
                              );
                            }}
                            rows={2}
                            placeholder="Descripción personalizada..."
                            className="w-full border border-brand-gray-200 rounded-lg px-3 py-1.5 font-sans text-sm focus:outline-none focus:border-brand-black resize-none"
                          />
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => {
                              setCombinacionesPersonalizadas((prev) => prev.filter((_, i) => i !== idx));
                            }}
                            className="w-7 h-7 flex items-center justify-center text-brand-gray-400 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors"
                            title="Eliminar combinación"
                          >
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {prendasQueCoincidenPaleta.length > 0 && (
              <div className="space-y-3 pt-4 border-t border-brand-gray-200">
                <p className="font-sans text-brand-gray-500 text-xs uppercase tracking-wider">
                  Prendas del armario que coinciden con la paleta
                </p>
                <div className="flex flex-wrap gap-2">
                  {prendasQueCoincidenPaleta.map((p) => (
                    <span
                      key={p.id}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-gray-200 font-sans text-xs bg-white"
                    >
                      <span
                        className="w-3 h-3 rounded-full border border-brand-gray-300/50 shrink-0"
                        style={{ backgroundColor: getColorHex(p.color) }}
                      />
                      {p.nombre} — {p.color}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AutoCombinaciones({ paletteColors }: { paletteColors: string[] }) {
  const autoCombos: { base: string; complemento: string; tono: string; desc: string }[] = [];
  for (let i = 0; i < paletteColors.length; i++) {
    for (let j = i + 1; j < paletteColors.length; j++) {
      const a = paletteColors[i];
      const b = paletteColors[j];
      if (neutralColors.includes(a) || neutralColors.includes(b)) {
        const base = neutralColors.includes(a) ? b : a;
        const comp = neutralColors.includes(a) ? a : b;
        autoCombos.push({
          base,
          complemento: comp,
          tono: 'Neutral',
          desc: `Base ${base.toLowerCase()} con toque ${comp.toLowerCase()} — combina bien con cualquier tono de piel.`,
        });
      }
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="font-sans text-brand-gray-500 text-xs uppercase tracking-wider">
          Combinaciones automáticas de la paleta
        </p>
        <span className="font-sans text-[11px] text-brand-gray-400">
          Generadas de {paletteColors.length} colores
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {autoCombos.slice(0, 4).map((combo, idx) => (
          <div key={idx} className="border border-brand-gray-200 rounded-xl p-4 bg-brand-cream/50">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex -space-x-2">
                <span
                  className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                  style={{ backgroundColor: getColorHex(combo.base) }}
                />
                <span
                  className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                  style={{ backgroundColor: getColorHex(combo.complemento) }}
                />
              </div>
              <div>
                <p className="font-sans text-sm font-medium text-brand-black">
                  {combo.base} + {combo.complemento}
                </p>
                <span className="font-sans text-[11px] text-brand-gray-400">{combo.tono}</span>
              </div>
            </div>
            <p className="font-sans text-xs text-brand-gray-500">{combo.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}